import React, { useMemo } from "react";
import ModalPortal from "@/components/Global/ModalPortal";
import { UserBadge } from "@/components/shared";
import useRosterInfo from "@/hooks/useRosterInfo";
import usePreTeachData from "@/hooks/usePreTeachData";
import "./StudentSelectionModal.scss";
import purpleCloseIcon from "@/images/icons/purple-close-icon.svg";
import prereqPlusIcon from "@/images/icons/prereq-plus-icon.svg";
import StatusCard from "./StatusCard";
import plusIcon from "@/images/icons/activity-card-plus-icon.svg"
import { MasteryPacing } from "@/enums/MasteryStatus";

interface StudentSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectStudent: (student: any) => void;
  excludeStudentIds: string[];
  prerequisiteSkillName: string;
  prerequisiteSkillId: string;
}

const StudentSelectionModal = ({
  isOpen,
  onClose,
  onSelectStudent,
  excludeStudentIds,
  prerequisiteSkillName,
  prerequisiteSkillId,
}: StudentSelectionModalProps) => {
  const { students } = useRosterInfo();
  const { getStudentMasteryForSkill } = usePreTeachData();

  // Filter out students that are already assigned to any prerequisite skill
  const availableStudents = useMemo(() => {
    if (!students) return [];

    return students.filter((student: any) =>
      !excludeStudentIds.includes(student.id)
    );
  }, [students, excludeStudentIds]);

  const handleStudentSelect = (student: any) => {
    const studentData = {
      studentId: student.id,
      studentName: `${student.first_name} ${student.last_name}`,
    };
    onSelectStudent(studentData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal
      size="content"
      isOpen={isOpen}
      onClose={onClose}
      style={{ borderRadius: "0.75rem", border: "1px solid #E1A8F0" }}
    >
      <div className="student-selection-modal d-flex flex-column">
        <div className="student-selection-body p-2">
          {/* <button
            className="btn-close-custom"
            onClick={onClose}
            aria-label="Close"
          >
          </button> */}
          {availableStudents.length === 0 ? (
            <div className="no-students-message text-center py-4">
              <p>No students available to add. All students have already been assigned to prerequisite skills.</p>
            </div>
          ) : (
            <div className="student-list">
              {availableStudents.map((student: any) => {
                const masteryData = getStudentMasteryForSkill(student.id, prerequisiteSkillId);

                return (
                  <div
                    key={student.id}
                    className="student-item d-flex align-items-center w-100"
                  >
                    <div className="student-avatar-section d-flex align-items-center">
                      <UserBadge name={`${student.first_name} ${student.last_name}`} />
                      <span className="student-name">
                        {student.first_name} {student.last_name}
                      </span>
                    </div>

                    <div className="d-flex align-items-center gap-4 flex-1">
                      <div className="d-flex flex-column align-items-center gap-4 flex-1">
                        <StatusCard
                          status={masteryData.pacing}
                          masteryStart={masteryData.pacing}
                          masteryEnd={masteryData.pacing}
                          observations={masteryData.exposureCount}
                          errors={masteryData.errorCount}
                          showGraph={false}
                          preReqModal={true}
                        />
                      </div>
                    </div>

                    <button
                      className="add-student-button-icon"
                      onClick={() => handleStudentSelect(student)}
                      aria-label={`Add ${student.first_name} ${student.last_name}`}
                    >
                      <img src={plusIcon} alt="add student" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default StudentSelectionModal;